import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { useCablePathsStandardized } from '@/hooks/entities/useCablePathsStandardized';
import { getFullPathPoints } from '@/services/cablePathService';
import { logger } from '@/lib/logger';

// Helper function to check if a line segment intersects a rectangle
function lineIntersectsRect(
  x1: number, y1: number, x2: number, y2: number,
  rectLeft: number, rectTop: number, rectRight: number, rectBottom: number
): boolean {
  // Check if either endpoint is inside the rectangle
  const p1Inside = x1 >= rectLeft && x1 <= rectRight && y1 >= rectTop && y1 <= rectBottom;
  const p2Inside = x2 >= rectLeft && x2 <= rectRight && y2 >= rectTop && y2 <= rectBottom;
  if (p1Inside || p2Inside) return true;

  // Check if line intersects any edge of the rectangle
  const intersectsLeft = lineSegmentIntersection(x1, y1, x2, y2, rectLeft, rectTop, rectLeft, rectBottom);
  const intersectsRight = lineSegmentIntersection(x1, y1, x2, y2, rectRight, rectTop, rectRight, rectBottom);
  const intersectsTop = lineSegmentIntersection(x1, y1, x2, y2, rectLeft, rectTop, rectRight, rectTop);
  const intersectsBottom = lineSegmentIntersection(x1, y1, x2, y2, rectLeft, rectBottom, rectRight, rectBottom);

  return intersectsLeft || intersectsRight || intersectsTop || intersectsBottom;
}

// Helper to check if two line segments intersect
function lineSegmentIntersection(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number
): boolean {
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (Math.abs(denom) < 1e-10) return false; // Parallel lines

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

export interface SelectionBoxRef {
  startSelection: (e: React.MouseEvent | React.TouchEvent) => void;
}

interface SelectionBoxProps {
  onSelectionStart?: () => void;
  onSelectionComplete?: () => void;
}

export const SelectionBox = forwardRef<SelectionBoxRef, SelectionBoxProps>(
  ({ onSelectionStart, onSelectionComplete }, ref) => {
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectionBox, setSelectionBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const isMouseDown = useRef(false);

    const selectMultipleEntities = useAppStore(state => state.selectMultipleEntities);
    const viewport = useAppStore(state => state.floorPlan.viewport);
    const { data, floorPlan, scaleData } = useFloorPlanContext();

    // Get cable data for selection
    const { junctions, junctionCoordMap } = useCableJunctionsStandardized(floorPlan?.id);
    const { paths: cablePaths } = useCablePathsStandardized(floorPlan?.id, junctionCoordMap, scaleData);

    const resetSelection = useCallback(() => {
      setIsSelecting(false);
      setSelectionBox({ x: 0, y: 0, width: 0, height: 0 });
      setStartPoint({ x: 0, y: 0 });
      isDragging.current = false;
      isMouseDown.current = false;
    }, []);

    const startSelection = useCallback((e: React.MouseEvent | React.TouchEvent) => {
      // Check if it's a touch event or mouse event
      const isTouch = 'touches' in e;

      // Only start selection on left click for mouse
      if (!isTouch && 'button' in e && e.button !== 0) return;

      // For touch events, only proceed if it's a single touch (ignore multi-touch)
      if (isTouch && (e as React.TouchEvent).touches.length !== 1) return;

      // Don't start if already selecting
      if (isMouseDown.current) return;

      // Don't interfere with fixture clicks or other interactive elements
      const target = e.target as HTMLElement;
      if (
        target.closest('[data-fixture-id]') ||
        target.closest('[data-control-id]') ||
        target.closest('[data-panel-id]') ||
        target.closest('[data-room-id]') ||
        target.closest('[data-product-id]') ||
        target.closest('[data-annotation-id]') ||
        target.closest('[data-junction-id]') ||
        target.closest('[data-path-id]') ||
        target.closest('[data-handle-type]') ||
        target.closest('[data-path-handles]')
      ) {
        return;
      }

      // Get viewport container
      const viewportEl = (e.currentTarget as HTMLElement).closest(
        '.floor-plan-viewport',
      ) as HTMLElement;
      if (!viewportEl) return;

      const rect = viewportEl.getBoundingClientRect();

      // Get coordinates based on event type
      const clientX = isTouch
        ? (e as React.TouchEvent).touches[0].clientX
        : (e as React.MouseEvent).clientX;
      const clientY = isTouch
        ? (e as React.TouchEvent).touches[0].clientY
        : (e as React.MouseEvent).clientY;

      // Store screen coordinates
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      setStartPoint({ x, y });
      setSelectionBox({ x, y, width: 0, height: 0 });
      isDragging.current = false;
      isMouseDown.current = true;

      /**
       * NOTE: We intentionally delay the `onSelectionStart` callback until the user has actually
       * moved the pointer far enough to begin a box-selection (see `handleMouseMove` / `handleTouchMove`).
       * This prevents a simple click (no drag) from being considered a selection in progress, which
       * previously blocked the viewport click handler from closing drawers on single taps/clicks.
       */
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        startSelection,
      }),
      [startSelection],
    );

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!isMouseDown.current) return;

        const viewportEl = document.querySelector('.floor-plan-viewport') as HTMLElement;
        if (!viewportEl) return;

        const rect = viewportEl.getBoundingClientRect();

        // Current screen coordinates
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;

        // Check if we've moved enough to start selection
        const distance = Math.sqrt(
          Math.pow(currentX - startPoint.x, 2) + Math.pow(currentY - startPoint.y, 2),
        );

        if (distance > 5) {
          // When the pointer has moved enough, we officially start the selection rectangle
          if (!isDragging.current) {
            // First time crossing the threshold – inform parent that selection is starting
            if (onSelectionStart) {
              onSelectionStart();
            }
          }

          isDragging.current = true;
          setIsSelecting(true);

          // Create selection box in screen coordinates
          const x = Math.min(startPoint.x, currentX);
          const y = Math.min(startPoint.y, currentY);
          const width = Math.abs(currentX - startPoint.x);
          const height = Math.abs(currentY - startPoint.y);

          setSelectionBox({ x, y, width, height });
        }
      },
      [startPoint, onSelectionStart],
    );

    const handleMouseUp = useCallback(
      (e: MouseEvent) => {
        if (!isMouseDown.current) return;

        // Process selection if we were dragging
        if (isDragging.current) {
          // Don't prevent events - let them bubble naturally
          // Find all entities within the selection box
          if (selectionBox.width > 5 && selectionBox.height > 5) {
            const viewportEl = document.querySelector('.floor-plan-viewport') as HTMLElement;
            if (!viewportEl) {
              logger.log('No viewport element found');
              resetSelection();
              return;
            }

            const viewportRect = viewportEl.getBoundingClientRect();

            /**
             * Coordinate Transformation:
             * 1. Selection box coordinates are in screen space (relative to viewport)
             * 2. Entity positions are in floor plan space (original image coordinates)
             * 3. We need to transform selection box to floor plan space for comparison
             *
             * Formula: floorPlanCoord = (screenCoord - viewportPosition) / scale
             */
            const selectionInFloorPlan = {
              x: (selectionBox.x - viewport.positionX) / viewport.scale,
              y: (selectionBox.y - viewport.positionY) / viewport.scale,
              width: selectionBox.width / viewport.scale,
              height: selectionBox.height / viewport.scale,
            };

            // 🔍 DIAGNOSTIC: Log coordinate transformation
            logger.log('[SelectionBox] 🎯 COORDINATE TRANSFORMATION:', {
              screenSpace: {
                x: selectionBox.x.toFixed(2),
                y: selectionBox.y.toFixed(2),
                width: selectionBox.width.toFixed(2),
                height: selectionBox.height.toFixed(2),
              },
              viewport: {
                positionX: viewport.positionX.toFixed(2),
                positionY: viewport.positionY.toFixed(2),
                scale: viewport.scale.toFixed(4),
              },
              floorPlanSpace: {
                x: selectionInFloorPlan.x.toFixed(2),
                y: selectionInFloorPlan.y.toFixed(2),
                width: selectionInFloorPlan.width.toFixed(2),
                height: selectionInFloorPlan.height.toFixed(2),
              },
              formula: {
                x: `(${selectionBox.x.toFixed(2)} - ${viewport.positionX.toFixed(2)}) / ${viewport.scale.toFixed(4)} = ${selectionInFloorPlan.x.toFixed(2)}`,
                y: `(${selectionBox.y.toFixed(2)} - ${viewport.positionY.toFixed(2)}) / ${viewport.scale.toFixed(4)} = ${selectionInFloorPlan.y.toFixed(2)}`,
              },
            });

            // Validate selection box calculations
            if (
              !isFinite(selectionInFloorPlan.x) ||
              !isFinite(selectionInFloorPlan.y) ||
              !isFinite(selectionInFloorPlan.width) ||
              !isFinite(selectionInFloorPlan.height)
            ) {
              logger.log('Invalid selection box calculations:', selectionInFloorPlan);
              resetSelection();
              return;
            }

            // Get all entity types from the context data
            const fixturesData = data.fixtures || [];
            const controlsData = data.controls || [];
            const productsData = data.lvProducts || [];
            const annotationsData = data.annotations || [];
            const roomsData = data.rooms || [];

            // Get AI tools state to check confidence threshold
            const aiToolsState = useAppStore.getState().aiTools;
            const confidenceThreshold = aiToolsState.confidenceThreshold;

            // Select fixtures based on their position data
            const selectedFixtureIds: string[] = [];

            fixturesData.forEach(fixture => {
              // Check if fixture meets confidence threshold (is visible)
              const confidence = fixture.confidence_score || 1;
              if (confidence < confidenceThreshold) {
                return; // Skip invisible fixtures
              }

              // Get fixture position and assume a size (radius) for intersection check
              const fixtureX = fixture.x_position;
              const fixtureY = fixture.y_position;
              const fixtureRadius = 15; // Approximate radius in floor plan coordinates

              // Check if fixture intersects with selection box (not just center point)
              const fixtureLeft = fixtureX - fixtureRadius;
              const fixtureRight = fixtureX + fixtureRadius;
              const fixtureTop = fixtureY - fixtureRadius;
              const fixtureBottom = fixtureY + fixtureRadius;

              const selectionLeft = selectionInFloorPlan.x;
              const selectionRight = selectionInFloorPlan.x + selectionInFloorPlan.width;
              const selectionTop = selectionInFloorPlan.y;
              const selectionBottom = selectionInFloorPlan.y + selectionInFloorPlan.height;

              // Check for intersection
              const intersects = !(
                fixtureRight < selectionLeft ||
                fixtureLeft > selectionRight ||
                fixtureBottom < selectionTop ||
                fixtureTop > selectionBottom
              );

              if (intersects) {
                selectedFixtureIds.push(fixture.id);
              }
            });

            // Select controls based on their position data
            const selectedControlIds: string[] = [];

            controlsData.forEach(control => {
              // Get control position and assume a size for intersection check
              const controlX = control.x;
              const controlY = control.y;
              const controlRadius = 20; // Approximate radius in floor plan coordinates

              // Check if control intersects with selection box (not just center point)
              const controlLeft = controlX - controlRadius;
              const controlRight = controlX + controlRadius;
              const controlTop = controlY - controlRadius;
              const controlBottom = controlY + controlRadius;

              const selectionLeft = selectionInFloorPlan.x;
              const selectionRight = selectionInFloorPlan.x + selectionInFloorPlan.width;
              const selectionTop = selectionInFloorPlan.y;
              const selectionBottom = selectionInFloorPlan.y + selectionInFloorPlan.height;

              // Check for intersection
              const intersects = !(
                controlRight < selectionLeft ||
                controlLeft > selectionRight ||
                controlBottom < selectionTop ||
                controlTop > selectionBottom
              );

              if (intersects) {
                selectedControlIds.push(control.id);
              }
            });

            // Select products based on their position data
            const selectedProductIds: string[] = [];

            productsData.forEach(product => {
              // Skip products that belong to a group
              if (product.parent_group_id) return;

              // Get product position and assume a size for intersection check
              const productX = product.x_position;
              const productY = product.y_position;
              const productRadius = 20; // Approximate radius in floor plan coordinates

              // Skip if position is invalid
              if (
                typeof productX !== 'number' ||
                typeof productY !== 'number' ||
                isNaN(productX) ||
                isNaN(productY)
              ) {
                logger.log(`Product ${product.id} has invalid position:`, {
                  x: productX,
                  y: productY,
                });
                return;
              }

              // Check if product intersects with selection box
              const productLeft = productX - productRadius;
              const productRight = productX + productRadius;
              const productTop = productY - productRadius;
              const productBottom = productY + productRadius;

              const selectionLeft = selectionInFloorPlan.x;
              const selectionRight = selectionInFloorPlan.x + selectionInFloorPlan.width;
              const selectionTop = selectionInFloorPlan.y;
              const selectionBottom = selectionInFloorPlan.y + selectionInFloorPlan.height;

              // Check for intersection
              const intersects = !(
                productRight < selectionLeft ||
                productLeft > selectionRight ||
                productBottom < selectionTop ||
                productTop > selectionBottom
              );

              // Additional validation to ensure we have valid numbers
              const isValidIntersection =
                !isNaN(productLeft) &&
                !isNaN(productRight) &&
                !isNaN(productTop) &&
                !isNaN(productBottom) &&
                !isNaN(selectionLeft) &&
                !isNaN(selectionRight) &&
                !isNaN(selectionTop) &&
                !isNaN(selectionBottom) &&
                isFinite(productLeft) &&
                isFinite(productRight) &&
                isFinite(productTop) &&
                isFinite(productBottom) &&
                isFinite(selectionLeft) &&
                isFinite(selectionRight) &&
                isFinite(selectionTop) &&
                isFinite(selectionBottom);

              if (intersects && isValidIntersection) {
                selectedProductIds.push(product.id);
              }
            });

            // Select annotations based on their position data
            const selectedAnnotationIds: string[] = [];

            annotationsData.forEach(annotation => {
              let annotationBounds = { left: 0, right: 0, top: 0, bottom: 0 };

              // Calculate bounds based on annotation type
              switch (annotation.type) {
                case 'line':
                case 'arrow':
                case 'measurement':
                  if (annotation.data.start && annotation.data.end) {
                    annotationBounds = {
                      left: Math.min(annotation.data.start.x, annotation.data.end.x),
                      right: Math.max(annotation.data.start.x, annotation.data.end.x),
                      top: Math.min(annotation.data.start.y, annotation.data.end.y),
                      bottom: Math.max(annotation.data.start.y, annotation.data.end.y),
                    };
                  }
                  break;
                case 'rectangle':
                case 'text':
                case 'image':
                  if (
                    annotation.data.x !== undefined &&
                    annotation.data.y !== undefined &&
                    annotation.data.width !== undefined &&
                    annotation.data.height !== undefined
                  ) {
                    annotationBounds = {
                      left: annotation.data.x,
                      right: annotation.data.x + annotation.data.width,
                      top: annotation.data.y,
                      bottom: annotation.data.y + annotation.data.height,
                    };
                  }
                  break;
                case 'circle':
                  if (
                    annotation.data.cx !== undefined &&
                    annotation.data.cy !== undefined &&
                    annotation.data.radius !== undefined
                  ) {
                    annotationBounds = {
                      left: annotation.data.cx - annotation.data.radius,
                      right: annotation.data.cx + annotation.data.radius,
                      top: annotation.data.cy - annotation.data.radius,
                      bottom: annotation.data.cy + annotation.data.radius,
                    };
                  }
                  break;
              }

              const selectionLeft = selectionInFloorPlan.x;
              const selectionRight = selectionInFloorPlan.x + selectionInFloorPlan.width;
              const selectionTop = selectionInFloorPlan.y;
              const selectionBottom = selectionInFloorPlan.y + selectionInFloorPlan.height;

              // Check for intersection
              const intersects = !(
                annotationBounds.right < selectionLeft ||
                annotationBounds.left > selectionRight ||
                annotationBounds.bottom < selectionTop ||
                annotationBounds.top > selectionBottom
              );

              if (intersects) {
                selectedAnnotationIds.push(annotation.id);
              }
            });

            // Select rooms based on their polygon data
            const selectedRoomIds: string[] = [];

            // Check if rooms are visible
            const roomsVisible = useAppStore.getState().rooms.roomsVisible;

            if (roomsVisible) {
              roomsData.forEach(room => {
                // Parse points if they're strings
                let points = room.points;
                if (typeof points === 'string') {
                  try {
                    points = JSON.parse(points);
                  } catch (e) {
                    return; // Skip if we can't parse
                  }
                }

                if (!Array.isArray(points) || points.length < 3) return;

                // Check if any room point is within selection box or if room intersects
                let intersects = false;

                // First check if any vertex is in selection box
                for (const point of points) {
                  if (
                    point.x >= selectionInFloorPlan.x &&
                    point.x <= selectionInFloorPlan.x + selectionInFloorPlan.width &&
                    point.y >= selectionInFloorPlan.y &&
                    point.y <= selectionInFloorPlan.y + selectionInFloorPlan.height
                  ) {
                    intersects = true;
                    break;
                  }
                }

                // If no vertices inside, check if selection box center is inside polygon
                if (!intersects) {
                  const centerX = selectionInFloorPlan.x + selectionInFloorPlan.width / 2;
                  const centerY = selectionInFloorPlan.y + selectionInFloorPlan.height / 2;

                  // Point in polygon test
                  let inside = false;
                  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
                    const xi = points[i].x,
                      yi = points[i].y;
                    const xj = points[j].x,
                      yj = points[j].y;

                    const isIntersect =
                      yi > centerY !== yj > centerY &&
                      centerX < ((xj - xi) * (centerY - yi)) / (yj - yi) + xi;
                    if (isIntersect) inside = !inside;
                  }

                  if (inside) intersects = true;
                }

                if (intersects) {
                  selectedRoomIds.push(room.id);
                }
              });
            }

            // Select cable junctions based on their position
            const selectedCableJunctionIds: string[] = [];

            if (junctions) {
              junctions.forEach(junction => {
                const junctionX = junction.x;
                const junctionY = junction.y;
                const junctionRadius = 16; // Approximate radius in floor plan coordinates

                const junctionLeft = junctionX - junctionRadius;
                const junctionRight = junctionX + junctionRadius;
                const junctionTop = junctionY - junctionRadius;
                const junctionBottom = junctionY + junctionRadius;

                const selectionLeft = selectionInFloorPlan.x;
                const selectionRight = selectionInFloorPlan.x + selectionInFloorPlan.width;
                const selectionTop = selectionInFloorPlan.y;
                const selectionBottom = selectionInFloorPlan.y + selectionInFloorPlan.height;

                // Check for intersection
                const intersects = !(
                  junctionRight < selectionLeft ||
                  junctionLeft > selectionRight ||
                  junctionBottom < selectionTop ||
                  junctionTop > selectionBottom
                );

                if (intersects) {
                  selectedCableJunctionIds.push(junction.id);
                }
              });
            }

            // Select cable paths based on their points
            const selectedCablePathIds: string[] = [];

            if (cablePaths && junctionCoordMap) {
              cablePaths.forEach(path => {
                // Get full points including junction coordinates
                const fullPoints = getFullPathPoints(path, junctionCoordMap);

                // Check if any point or line segment intersects with selection box
                const selectionLeft = selectionInFloorPlan.x;
                const selectionRight = selectionInFloorPlan.x + selectionInFloorPlan.width;
                const selectionTop = selectionInFloorPlan.y;
                const selectionBottom = selectionInFloorPlan.y + selectionInFloorPlan.height;

                let intersects = false;

                // Check if any point is inside selection box
                for (const point of fullPoints) {
                  if (
                    point.x >= selectionLeft &&
                    point.x <= selectionRight &&
                    point.y >= selectionTop &&
                    point.y <= selectionBottom
                  ) {
                    intersects = true;
                    break;
                  }
                }

                // If no points inside, check if any line segment intersects
                if (!intersects && fullPoints.length >= 2) {
                  for (let i = 0; i < fullPoints.length - 1; i++) {
                    const p1 = fullPoints[i];
                    const p2 = fullPoints[i + 1];

                    // Simple line-rect intersection check
                    // Check if the line segment intersects the selection rectangle
                    const lineIntersects = lineIntersectsRect(
                      p1.x, p1.y, p2.x, p2.y,
                      selectionLeft, selectionTop, selectionRight, selectionBottom
                    );

                    if (lineIntersects) {
                      intersects = true;
                      break;
                    }
                  }
                }

                if (intersects) {
                  selectedCablePathIds.push(path.id);
                }
              });
            }

            const totalSelections =
              selectedFixtureIds.length +
              selectedControlIds.length +
              selectedProductIds.length +
              selectedAnnotationIds.length +
              selectedRoomIds.length +
              selectedCableJunctionIds.length +
              selectedCablePathIds.length;
            logger.log('Total selections:', totalSelections);
            logger.log(
              'Products selected:',
              selectedProductIds.length,
              'out of',
              productsData.length,
            );
            logger.log('Selection box:', selectionInFloorPlan);

            if (totalSelections > 0) {
              // Check if we're in circuit mode
              const toolbarState = useAppStore.getState().ui.toolbar;
              const isCircuitMode = toolbarState.activeMode === 'circuit';

              // Get current selections
              const currentSelectedFixtures = useAppStore.getState().selection.fixtures;
              const currentSelectedControls = useAppStore.getState().selection.controls;

              if (isCircuitMode) {
                // In circuit mode, add to existing selection (additive only for drag selection)
                // Get fixtures that are new (not already selected)
                const newFixtureIds = selectedFixtureIds.filter(
                  id => !currentSelectedFixtures.has(id),
                );
                const newControlIds = selectedControlIds.filter(
                  id => !currentSelectedControls.has(id),
                );

                if (newFixtureIds.length > 0 || newControlIds.length > 0) {
                  useAppStore.setState(state => {
                    // Add new fixtures to selection (don't remove existing)
                    newFixtureIds.forEach(id => state.selection.fixtures.add(id));

                    // For controls in circuit mode, we typically don't select them
                    // but if needed, add them too
                    newControlIds.forEach(id => state.selection.controls.add(id));
                  });

                  logger.log(`Added ${newFixtureIds.length} new fixtures to selection`);
                } else {
                  logger.log('No new fixtures to add to selection');
                }
              } else {
                // Normal mode - replace selection
                const clearAllSelections = useAppStore.getState().clearAllSelections;
                clearAllSelections();

                // Update selections - need to do this in a way that doesn't overwrite
                // Since the store uses immer, we can't directly assign Sets
                // We need to clear and then add items
                useAppStore.setState(state => {
                  // Clear existing selections
                  state.selection.fixtures.clear();
                  state.selection.controls.clear();
                  state.selection.products.clear();
                  state.selection.annotations.clear();
                  state.selection.rooms.clear();
                  state.selection.cablePaths.clear();
                  state.selection.cableJunctions.clear();

                  // Add new selections
                  selectedFixtureIds.forEach(id => state.selection.fixtures.add(id));
                  selectedControlIds.forEach(id => state.selection.controls.add(id));
                  selectedProductIds.forEach(id => state.selection.products.add(id));
                  selectedAnnotationIds.forEach(id => state.selection.annotations.add(id));
                  selectedRoomIds.forEach(id => state.selection.rooms.add(id));
                  selectedCablePathIds.forEach(id => state.selection.cablePaths.add(id));
                  selectedCableJunctionIds.forEach(id => state.selection.cableJunctions.add(id));
                });

                logger.log(
                  `Selected ${selectedFixtureIds.length} fixtures, ${selectedControlIds.length} controls, ${selectedProductIds.length} products, ${selectedAnnotationIds.length} annotations, ${selectedRoomIds.length} rooms, ${selectedCablePathIds.length} cable paths, and ${selectedCableJunctionIds.length} cable junctions`,
                );
              }

              // Small delay to ensure state update is processed
              setTimeout(() => {
                // Check if we're in circuit mode
                const toolbarState = useAppStore.getState().ui.toolbar;
                const isCircuitMode = toolbarState.activeMode === 'circuit';

                // Don't open drawers in circuit mode
                if (!isCircuitMode) {
                  // Open appropriate drawer
                  const setActiveDrawer = useAppStore.getState().setActiveDrawer;

                  // Double check selections were saved
                  const finalFixtureCount = useAppStore.getState().selection.fixtures.size;
                  const finalControlCount = useAppStore.getState().selection.controls.size;
                  const finalProductCount = useAppStore.getState().selection.products.size;
                  const finalAnnotationCount = useAppStore.getState().selection.annotations.size;
                  const finalRoomCount = useAppStore.getState().selection.rooms.size;
                  const finalCablePathCount = useAppStore.getState().selection.cablePaths.size;
                  const finalCableJunctionCount = useAppStore.getState().selection.cableJunctions.size;
                  const finalCableCount = finalCablePathCount + finalCableJunctionCount;
                  const finalTotal =
                    finalFixtureCount +
                    finalControlCount +
                    finalProductCount +
                    finalAnnotationCount +
                    finalRoomCount +
                    finalCableCount;

                  // Only skip drawer if ALL selections are annotations
                  const hasOnlyAnnotations =
                    finalAnnotationCount > 0 && finalAnnotationCount === finalTotal;

                  // Check if we have only cable selections (paths and/or junctions)
                  const hasOnlyCables = finalCableCount > 0 && finalCableCount === finalTotal;

                  if (finalTotal > 1 && !hasOnlyAnnotations) {
                    // Open cable drawer for multiple cable items
                    if (hasOnlyCables) {
                      logger.log('Opening cable multi-selection drawer');
                      // For multiple cable items, open the cable path drawer which will show bulk edit options
                      setActiveDrawer('cablePath');
                    } else {
                      logger.log('Opening multi-selection drawer');
                      setActiveDrawer('selection'); // Multi-item selection
                    }
                  } else if (finalFixtureCount === 1) {
                    setActiveDrawer('fixture');
                  } else if (finalControlCount === 1) {
                    setActiveDrawer('control');
                  } else if (finalProductCount === 1) {
                    setActiveDrawer('product');
                  } else if (finalAnnotationCount === 1) {
                    setActiveDrawer('annotation');
                  } else if (finalRoomCount === 1) {
                    setActiveDrawer('room');
                  } else if (finalCablePathCount === 1 && finalCableJunctionCount === 0) {
                    setActiveDrawer('cablePath');
                  } else if (finalCableJunctionCount === 1 && finalCablePathCount === 0) {
                    setActiveDrawer('cableJunction');
                  } else if (finalCableCount > 0) {
                    // Multiple cable items selected, open cable path drawer for bulk editing
                    setActiveDrawer('cablePath');
                  }
                }
              }, 0);
            }
          }

          // Notify parent that selection is complete (whether items were selected or not)
          if (onSelectionComplete) {
            onSelectionComplete();
          }
        }

        // Always reset after mouse up
        resetSelection();
      },
      [
        selectionBox,
        selectMultipleEntities,
        resetSelection,
        viewport,
        data.fixtures,
        data.controls,
        onSelectionComplete,
      ],
    );

    // Handle touch move
    const handleTouchMove = useCallback(
      (e: TouchEvent) => {
        // Ignore if multi-touch (2-finger pan, pinch, etc)
        if (!isMouseDown.current || e.touches.length !== 1) return;

        const viewportEl = document.querySelector('.floor-plan-viewport') as HTMLElement;
        if (!viewportEl) return;

        const rect = viewportEl.getBoundingClientRect();

        // Current screen coordinates
        const currentX = e.touches[0].clientX - rect.left;
        const currentY = e.touches[0].clientY - rect.top;

        // Check if we've moved enough to start selection
        const distance = Math.sqrt(
          Math.pow(currentX - startPoint.x, 2) + Math.pow(currentY - startPoint.y, 2),
        );

        if (distance > 5) {
          if (!isDragging.current) {
            if (onSelectionStart) {
              onSelectionStart();
            }
          }

          isDragging.current = true;
          setIsSelecting(true);

          // Create selection box in screen coordinates
          const x = Math.min(startPoint.x, currentX);
          const y = Math.min(startPoint.y, currentY);
          const width = Math.abs(currentX - startPoint.x);
          const height = Math.abs(currentY - startPoint.y);

          setSelectionBox({ x, y, width, height });
        }

        e.preventDefault();
      },
      [startPoint, onSelectionStart],
    );

    // Handle touch end
    const handleTouchEnd = useCallback(
      (e: TouchEvent) => {
        if (!isMouseDown.current) return;

        // Process selection if we were dragging
        if (isDragging.current) {
          // Convert TouchEvent to MouseEvent-like object for handleMouseUp
          const fakeMouseEvent = {
            stopPropagation: () => {},
            preventDefault: () => {},
          } as MouseEvent;

          handleMouseUp(fakeMouseEvent);
        } else {
          // Just reset if it was a tap
          resetSelection();
        }
      },
      [handleMouseUp, resetSelection],
    );

    // Global event listeners
    useEffect(() => {
      const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e);
      const handleGlobalMouseUp = (e: MouseEvent) => handleMouseUp(e);
      const handleGlobalTouchMove = (e: TouchEvent) => handleTouchMove(e);
      const handleGlobalTouchEnd = (e: TouchEvent) => handleTouchEnd(e);

      // Add listeners
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);

      // Also handle mouse leave to reset if mouse leaves the window
      const handleMouseLeave = () => {
        if (isMouseDown.current) {
          resetSelection();
        }
      };
      document.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.removeEventListener('touchmove', handleGlobalTouchMove);
        document.removeEventListener('touchend', handleGlobalTouchEnd);
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd, resetSelection]);

    // Reset on escape key
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isSelecting) {
          resetSelection();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isSelecting, resetSelection]);

    return (
      <>
        {/* Selection box visual - in screen coordinates */}
        {isSelecting && selectionBox.width > 0 && selectionBox.height > 0 && (
          <div
            className="absolute border-2 border-blue-500 bg-blue-500/20 pointer-events-none"
            style={{
              left: `${selectionBox.x}px`,
              top: `${selectionBox.y}px`,
              width: `${selectionBox.width}px`,
              height: `${selectionBox.height}px`,
              zIndex: 9999,
              mixBlendMode: 'multiply',
            }}
          />
        )}
      </>
    );
  },
);

SelectionBox.displayName = 'SelectionBox';
